import * as React from "react"
import { useTranslation } from "react-i18next"
import { Link, useLocation } from "react-router-dom"
import { ClipboardCheck, Trophy } from "lucide-react"
import { cn } from "../lib/utils"
import { auth } from "../config/firebase"
import { Button } from "./ui/button"
import { Avatar, AvatarFallback, AvatarImage } from "./ui/avatar"
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "./ui/dropdown-menu"
import { AppFooter } from "./app-footer"
import {
  DashboardIcon,
  FileTextIcon,
  UsersIcon,
  LogOutIcon,
  MenuIcon,
  XIcon,
  UserIcon,
  SettingsIcon,
  OrgChartIcon,
  VacationIcon,
} from "./icons"
import { useIsAdmin, useIsResourceAdmin, useIsManager, useUnassignedGustoUsers, useGustoEnabled } from '../hooks'
import { UnassignedCountBadge } from './gusto/UnassignedCountBadge'
import { getRuntimeEnv } from '../utils/runtimeEnv'
// import { ProfileUpdateDialog } from "./profile-update-dialog"

interface AppShellUser {
  id: string
  name: string
  email: string
  profilePictureUrl?: string
  organizationRole?: string
  // Legacy fields for compat
  displayName?: string
  photoURL?: string
}

interface AppShellProps {
  children: React.ReactNode
  user?: AppShellUser | null
  onSignOut?: () => void
}

const navItems = [
  { href: "/dashboard", label: "Dashboard", icon: DashboardIcon },
  { href: "/invoices", label: "Invoices", icon: FileTextIcon },
  { href: "/vacations", label: "PTO", icon: VacationIcon },
  { href: "/people", label: "People", icon: UsersIcon, adminOnly: true, managerVisible: true },
  { href: "/performance-reviews", label: "Reviews", icon: ClipboardCheck, resourceAdminOnly: true, managerVisible: true },
  { href: "/org-chart", label: "Org Chart", icon: OrgChartIcon },
  { href: "/settings", label: "Settings", icon: SettingsIcon, adminOnly: true },
]

const getProdeUrl = (): string | undefined => getRuntimeEnv('REACT_APP_PRODE_URL')

/**
 * Prode integration kill switch. The Prode app resources are currently
 * decommissioned, so the button is hidden unless the flag is explicitly
 * set to "true" (allowing the integration to be re-enabled in the future).
 */
const isProdeEnabled = (): boolean => getRuntimeEnv('REACT_APP_PRODE_ENABLED') === 'true'

export function AppShell({ children, user, onSignOut }: AppShellProps) {
  const { t } = useTranslation('common')
  const location = useLocation()
  const pathname = location.pathname
  const [mobileMenuOpen, setMobileMenuOpen] = React.useState(false)
  // const [profileDialogOpen, setProfileDialogOpen] = React.useState(false)

  const prodeUrl = getProdeUrl()
  const prodeEnabled = isProdeEnabled() && Boolean(prodeUrl)

  const handleOpenProde = React.useCallback(async () => {
    const firebaseUser = auth.currentUser
    if (!firebaseUser) return

    try {
      const token = await firebaseUser.getIdToken(true)
      window.open(`${prodeUrl}/auth/callback?token=${token}`, '_blank')
    } catch (error) {
      console.error('Error getting Firebase token:', error)
    }
  }, [prodeUrl])

  const { isAdmin, isLoading: adminLoading } = useIsAdmin()
  const { isAdmin: isResourceAdmin, isLoading: resourceAdminLoading } = useIsResourceAdmin()
  const { isManager } = useIsManager()

  const isPrivileged = isAdmin || isManager
  const { isConnected: gustoConnected } = useGustoEnabled()
  const { data: unassignedGustoUsers } = useUnassignedGustoUsers(isPrivileged && gustoConnected)
  const unassignedCount = unassignedGustoUsers?.length ?? 0

  const getNavLabel = (item: typeof navItems[number]): string => {
    if (!item.managerVisible || !isManager) return item.label
    if (item.href === "/people" && !isAdmin && !adminLoading) return "My Team"
    if (item.href === "/performance-reviews" && !isResourceAdmin && !resourceAdminLoading) return "Team Reviews"
    return item.label
  }

  const filteredNavItems = navItems.filter((item) => {
    // Reviews follow the backend's resource-admin threshold: OWNER/ADMIN, not BILLING.
    if (item.resourceAdminOnly) return isResourceAdmin || (item.managerVisible === true && isManager)
    if (!item.adminOnly) return true
    if (isAdmin) return true
    if (item.managerVisible && isManager) return true
    return false
  })

  return (
    <div className="flex min-h-screen flex-col bg-background" data-testid="app-shell">
      {/* Header */}
      <header className="sticky top-0 z-50 border-b border-border/40 bg-background/80 backdrop-blur-md" data-testid="app-header">
        <div className="mx-auto flex h-16 max-w-7xl items-center justify-between px-4 sm:px-6 lg:px-8">
          {/* Logo and Nav */}
          <div className="flex items-center gap-8">
            <Link to="/dashboard" className="flex items-center gap-2 group" data-testid="nav-link-logo">
              <img 
                src="/prosigliere-logo-1.png" 
                alt="Prosigliere" 
                className="h-8 w-auto group-hover:opacity-80 transition-opacity"
              />
              <span className="hidden font-semibold text-foreground sm:inline-block group-hover:text-primary transition-colors">Prosigliere</span>
            </Link>

            {/* Desktop Navigation */}
            <nav className="hidden items-center gap-1 md:flex" data-testid="nav-desktop">
              {filteredNavItems.map((item) => {
                const isActive = pathname === item.href || pathname?.startsWith(`${item.href}/`)
                const Icon = item.icon
                const label = getNavLabel(item)
                return (
                  <Link
                    key={item.href}
                    to={item.href}
                    data-testid={`nav-link-${item.href.replace('/', '')}`}
                    className={cn(
                      "flex items-center gap-2 rounded-lg px-3 py-2 text-sm font-medium transition-all duration-200",
                      isActive
                        ? "bg-primary/20 text-primary shadow-sm shadow-primary/20"
                        : "text-muted-foreground hover:bg-accent hover:text-foreground",
                    )}
                  >
                    <Icon />
                    {label}
                    {item.href === '/people' && (
                      <UnassignedCountBadge count={unassignedCount} className="ml-1" />
                    )}
                  </Link>
                )
              })}
            </nav>
          </div>

          {/* Right Side */}
          <div className="flex items-center gap-3">
            {/* Prode Button */}
            {prodeEnabled && (
              <Button
                variant="outline"
                size="sm"
                onClick={handleOpenProde}
                data-testid="nav-button-prode"
                className="hidden sm:flex items-center gap-2"
              >
                <Trophy className="h-4 w-4" />
                {t('nav.openProde')}
              </Button>
            )}
            {prodeEnabled && (
              <Button
                variant="ghost"
                size="icon"
                onClick={handleOpenProde}
                data-testid="nav-button-prode-mobile"
                className="sm:hidden"
              >
                <Trophy className="h-4 w-4" />
              </Button>
            )}

            {/* Mobile Menu Button */}
            <Button
              variant="ghost"
              size="icon"
              className="md:hidden"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              data-testid="nav-button-mobile-menu"
            >
              {mobileMenuOpen ? <XIcon /> : <MenuIcon />}
            </Button>

            {/* User Menu */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" className="relative h-9 w-9 rounded-full" data-testid="nav-button-user-menu">
                  <Avatar className="h-9 w-9 ring-2 ring-white/20 shadow-lg shadow-white/10">
                    {(user?.profilePictureUrl || user?.photoURL) ? (
                      <AvatarImage src={user.profilePictureUrl || user.photoURL || "/placeholder.svg"} alt={user.name} />
                    ) : null}
                    <AvatarFallback className="bg-primary/10 text-primary font-medium">
                      {user?.name?.charAt(0) || "U"}
                    </AvatarFallback>
                  </Avatar>
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56" data-testid="nav-user-menu-content">
                <DropdownMenuLabel data-testid="nav-user-menu-label">
                  <div className="flex flex-col space-y-1">
                    <p className="text-sm font-medium" data-testid="nav-user-display-name">{user?.name}</p>
                    <p className="text-xs text-muted-foreground" data-testid="nav-user-email">{user?.email}</p>
                    <p className="text-xs text-muted-foreground capitalize" data-testid="nav-user-role">{user?.organizationRole}</p>
                  </div>
                </DropdownMenuLabel>
                <DropdownMenuSeparator />
                <DropdownMenuItem asChild data-testid="nav-menu-item-profile">
                  <Link to="/my-profile" className="cursor-pointer">
                    <UserIcon />
                    {t('nav.updateProfile')}
                  </Link>
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={onSignOut} className="text-destructive focus:text-destructive" data-testid="nav-menu-item-signout">
                  <LogOutIcon />
                  {t('buttons.signOut')}
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>

        {/* Mobile Navigation */}
        {mobileMenuOpen && (
          <nav className="border-t bg-card px-4 py-3 md:hidden" data-testid="nav-mobile">
            <div className="flex flex-col gap-1">
              {filteredNavItems.map((item) => {
                const isActive = pathname === item.href || pathname?.startsWith(`${item.href}/`)
                const Icon = item.icon
                const label = getNavLabel(item)
                return (
                  <Link
                    key={item.href}
                    to={item.href}
                    onClick={() => setMobileMenuOpen(false)}
                    data-testid={`nav-mobile-link-${item.href.replace('/', '')}`}
                    className={cn(
                      "flex items-center gap-2 rounded-lg px-3 py-2 text-sm font-medium transition-colors",
                      isActive
                        ? "bg-primary/10 text-primary"
                        : "text-muted-foreground hover:bg-muted hover:text-foreground",
                    )}
                  >
                    <Icon />
                    {label}
                    {item.href === '/people' && (
                      <UnassignedCountBadge
                        count={unassignedCount}
                        className="ml-1"
                        data-testid="gusto-unassigned-badge-mobile"
                      />
                    )}
                  </Link>
                )
              })}
            </div>
          </nav>
        )}
      </header>

      {/* Main Content */}
      <main className="mx-auto w-full max-w-7xl flex-1 px-4 py-8 sm:px-6 lg:px-8">{children}</main>
      
      {/* Footer */}
      <AppFooter />
      
      {/* Profile Update Dialog */}
      {/* <ProfileUpdateDialog open={profileDialogOpen} onOpenChange={setProfileDialogOpen} /> */}
    </div>
  )
}
